import type { Story } from '@/mocks/featuredStories';
import Section from './Section';
import StoryCover from './StoryCover';

interface Props {
  stories: Story[];
  onOpen: (s: Story) => void;
}

function parseHeat(h: string) {
  const n = parseFloat(h.replace('万', ''));
  return Number.isFinite(n) ? n : 60;
}

export default function Yearly({ stories, onOpen }: Props) {
  const max = Math.max(...stories.map((s) => parseHeat(s.heat)));
  return (
    <Section
      id="yearly"
      icon="ri-trophy-line"
      title="必吃大瓜 · 年度盘点"
      en="YEAR-IN-REVIEW 2026"
      desc="一年一度的编辑部盘点开始预热：从综艺翻红、口碑黑马到模式出海，编辑部圈出年度值得回味的大事件。年度盘点专题正在陆续放送，蹲住别走。"
      right={
        <a
          href="#feed"
          className="flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-3.5 py-1.5 text-xs font-semibold text-foreground-950 transition hover:bg-accent-600"
        >
          加入年度投票 <i className="ri-bar-chart-fill" />
        </a>
      }
    >
      <div className="divide-y divide-background-200 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
        {stories.map((s, i) => {
          const w = Math.round((parseHeat(s.heat) / max) * 100);
          const top = i < 3;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onOpen(s)}
              className="group flex w-full items-center gap-3 px-3 py-3.5 text-left transition hover:bg-primary-50/60 md:gap-5 md:px-5"
            >
              <span
                className={`w-10 shrink-0 text-center font-heading text-2xl font-black md:text-3xl ${
                  top ? 'text-primary-500' : 'text-background-400 group-hover:text-primary-400'
                }`}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="hidden h-16 w-24 shrink-0 overflow-hidden rounded-lg md:block">
                <StoryCover story={s} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {top && (
                    <span className="flex items-center gap-0.5 rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-bold text-accent-800">
                      <i className="ri-medal-fill" /> 领跑
                    </span>
                  )}
                  <span className="rounded bg-background-200 px-1.5 text-[11px] text-foreground-600">{s.category}</span>
                </div>
                <h3 className="mt-1 line-clamp-1 text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700 md:text-base">
                  {s.title}
                </h3>
                <div className="mt-1.5 hidden h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-background-100 md:block">
                  <div
                    className={`h-full rounded-full ${top ? 'bg-primary-500' : 'bg-accent-400'}`}
                    style={{ width: `${Math.max(22, w)}%` }}
                  />
                </div>
              </div>
              <div className="hidden shrink-0 text-right sm:block">
                <p className="flex items-center justify-end gap-1 text-sm font-semibold text-accent-700">
                  <i className="ri-fire-fill" /> {s.heat}
                </p>
                <p className="text-xs text-foreground-500">热度</p>
              </div>
              <i className="shrink-0 text-xl text-foreground-300 transition group-hover:text-primary-500 ri-arrow-right-s-line" />
            </button>
          );
        })}
      </div>
    </Section>
  );
}